/**
 * Keeps an accordion's own header under the cursor when it is toggled.
 *
 * The PDP accordions sit in a `position: sticky` buy column. Opening one makes the column outgrow
 * the gallery, so the flex row grows to match, the column's sticky slack collapses to zero and the
 * whole column snaps back to its in-flow position — measured at 162px on a Daytona PDP, upward on
 * open and downward on close. The row the shopper clicked leaves the viewport in a single frame.
 *
 * Rather than give up the sticky column, the document scroll absorbs the shift.
 */
const TOGGLE_SELECTOR = '[data-modal-role~="toggle"]'

let pending: { button: Element; top: number } | null = null

/**
 * The sticky collapse is the whole mechanism, so a sticky ancestor is required — and a fixed or
 * internally-scrolling ancestor disqualifies the toggle, because there the button can move without
 * the document having anything to do with it and scrolling the page would be the wrong correction.
 * That covers the nav dropdowns and filter drawers that share x-modal with the accordions.
 */
const isDocumentAnchored = (element: Element): boolean => {
  let node: Element | null = element
  let sticky = false

  while (node && node !== document.body) {
    const styles = getComputedStyle(node)
    if (styles.position === 'fixed') return false
    if (styles.position === 'sticky') sticky = true
    const scrolls = /^(auto|scroll)$/.test(styles.overflowY) && node.scrollHeight > node.clientHeight
    if (scrolls) return false
    node = node.parentElement
  }

  return sticky
}

const findToggle = (event: Event): Element | null => {
  const target = event.target as Element | null
  const button = target?.closest?.(TOGGLE_SELECTOR)
  if (!button || !button.closest('x-modal')) return null
  return isDocumentAnchored(button) ? button : null
}

// x-modal binds its toggle handler to the button itself, so capture runs before the state flips
// and bubble runs after it. Reading the position in each is what makes the correction synchronous:
// a requestAnimationFrame would paint the jump first, and never fires at all in a hidden tab.
document.addEventListener(
  'click',
  (event) => {
    const button = findToggle(event)
    pending = button ? { button, top: button.getBoundingClientRect().top } : null
  },
  true
)

document.addEventListener('click', (event) => {
  if (!pending || findToggle(event) !== pending.button) {
    pending = null
    return
  }

  const delta = pending.button.getBoundingClientRect().top - pending.top
  pending = null
  if (Math.abs(delta) < 1) return

  // `instant` is explicit: `html { scroll-behavior: smooth }` would otherwise animate the fix.
  window.scrollBy({ top: delta, behavior: 'instant' })
})
